import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { of, Subscription } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';
import { MerchandiseCodes } from 'src/app/core/enums/merchandiseCodes.enum';
import { GeoValidationService } from 'src/app/core/services/geo-validation.service';
import { ThirdPartyAddress } from 'src/app/core/interfaces/thirdParty';
import { ThirdPartyConsignmentService } from 'src/app/modules/cargo/manual-creation-cargo/components/third-party-consignment/third-party-consignment.service';
import { AddressCargo } from 'src/app/core/interfaces/addressCargo';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { CatalogItem } from 'src/app/core/interfaces/catalogItem';
import { LocationAddress } from 'src/app/core/interfaces/locationAddress';
import { MerchandiseType } from 'src/app/core/interfaces/merchandiseType';
import { ThirdParty } from 'src/app/core/interfaces/thirdParty';
import { DateManager } from 'src/app/core/managers/date.manager';
import { Global } from 'src/app/core/resources/global';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Utils } from 'src/app/core/resources/utils';
import { TypeMerchandiseService } from 'src/app/modules/cargo/manual-creation-cargo/components/type-merchandise/type-merchandise.service';
import { ModelCargo } from 'src/app/modules/cargo/manual-creation-cargo/resources/modelCargo';
import { MenuServiceService } from 'src/app/modules/service-request-form/menu-service/menu-service.service';
import { ConsignmentManager } from 'src/app/core/managers/consignment.manager';
import { FreightListService } from 'src/app/modules/cargo/cargo-list/cargo-list.service';
import { Catalog } from 'src/app/core/interfaces/catalog';
import { ConsignmentCargo } from 'src/app/core/interfaces/consignmentCargo';

export type ConsignmentAddressOption = Pick<AddressCargo, 'address' | 'location' | 'time'> & { index: number, branchOffice: string };
export interface ConsignmentCityOption {
  municipalityCode: string;
  name: string;
  countryCode: string;
  date: string;
  index: number;
  type?: 'origin' | 'destination';
  thirdParties?: ConsignmentThirdPartyOption[];
}

export interface ConsignmentThirdPartyOption {
  name?: string;
  documentNumber?: string;
  documentType?: string;
  addresses?: ConsignmentAddressOption[];
}

@Component({
  selector: 'app-consignment-card',
  templateUrl: './consignment-card.component.html',
  styleUrls: ['./consignment-card.component.scss'],
  providers: [ModelCargo, ConsignmentManager]
})
export class ConsignmentCardComponent implements OnInit, OnDestroy {
  @Input() form: FormGroup;
  @Input() title: string;
  @Input() formValidate: string;
  @Input() senderCityOptions: { [key: string]: ConsignmentCityOption };
  @Input() recipientCityOptions: { [key: string]: ConsignmentCityOption };
  @Input() canDeleteConsignment: boolean = true;
  @Input() cargo: Cargo;
  @Output() removeConsignmentForm = new EventEmitter<void>();

  permission = Permission;
  today = new Date();
  readonly maxDate: Date = DateManager.add(new Date(), 30, 'days');
  consolidationTypeOptions: CatalogItem[] = [];
  internalForm = new FormGroup({
    cargoOwnerNameControl: new FormControl({ value: '-', disabled: true }),
    cargoOwnerDocumentControl: new FormControl({ value: '-', disabled: true }),
    cargoOwnerBranchOfficeControl: new FormControl({ value: '-', disabled: true }),
    operationTypeControl: new FormControl('', Validators.required),
    consolidationTypeControl: new FormControl('', Validators.required),
    cargoNatureControl: new FormControl('', Validators.required),
    packagingTypeControl: new FormControl('', Validators.required),
    merchandiseControl: new FormControl('', Validators.required),
    chapterControl: new FormControl({ value: '', disabled: true }),
    senderThirdParty: new FormControl('', Validators.required),
    senderCityControl: new FormControl('', Validators.required),
    senderAddressControl: new FormControl('', Validators.required),
    loadDateControl: new FormControl('', Validators.required),
    loadTimePactControl: new FormControl('', Validators.required),
    recipientThirdParty: new FormControl('', Validators.required),
    recipientCityControl: new FormControl('', Validators.required),
    recipientAddressControl: new FormControl('', Validators.required),
    unloadDateControl: new FormControl('', Validators.required),
    unloadTimePactControl: new FormControl('', Validators.required),
    // Mercancías peligrosas
    subHeaderControl: new FormControl('', Validators.required),
    tariffControl: new FormControl('', Validators.required),
    UNCodeControl: new FormControl('', Validators.required),
    UNDescriptionControl: new FormControl({ value: '', disabled: true }, Validators.required),
    productTypeStateControl: new FormControl('', Validators.required),
    packagingGroupControl: new FormControl('', Validators.required),
    hazardousWasteCurrentControl: new FormControl('', Validators.required),
    hazardousWasteCurrentDescriptionControl: new FormControl('', Validators.required),
    disintegrationControl: new FormControl('', Validators.required),
    totalWeightControl: new FormControl(''),
    weightUnitControl: new FormControl(''),
  });
  readonly consignmentTypeOptions: Array<{ code: string, description: string, tooltip: string }> = this.utils.clone(this.menuService.consignmentTypes);
  readonly cargoNatureTypeOptions: Array<{ code: string, description: string }> = this.utils.clone(this.menuService.cargoNatureTypes);
  readonly packagingTypeOptions: Array<{ code: string, description: string, hidden?: boolean }> = this.utils.clone(this.modelCargo.packagingTypes);
  timePactOptions = this.utils.clone(this.menuService.timePactOptions);
  loadTimePactOptions = this.timePactOptions;
  unloadTimePactOptions = this.timePactOptions;
  readonly timeList: Array<{ value: string, title: string }> = this.utils.getListTime();
  filteredConsignmentTypeOptions: Array<{ code: string, description: string, tooltip: string }> = [];
  filteredPackagingTypeOptions: Array<{ code: string, description: string }> = [];
  UNCodeOptions = [{ code: '1', description: 'UNCode 1' }, { code: '2', description: 'UNCode N.E.P.' }, { code: '3', description: 'UNCode 3' }];
  productTypeStates = this.utils.clone(this.global.productTypeStates);
  readonly packagingOptions = this.utils.clone(this.global.packagingOptions);
  packagingGroupOptions: Array<{ code: string, description: string }> = [];
  dangerousCharacteristicsOptions: Array<{ code: string, description: string }> = this.utils.clone(this.global.dangerousCharacteristics);
  hazardousWasteCurrentOptions = ['Y', 'A'];
  weightUnitOptions = [
    { code: 'KGM', description: 'Kilogramo' },
    { code: 'GLL', description: 'Galón' },
    { code: 'MTQ', description: 'Metro Cúbico' },
    { code: 'CMQ', description: 'Centímetro Cúbico' },
    { code: 'LTR', description: 'Litro' },
    { code: 'MLT', description: 'Mililitro' },
    { code: 'BLL', description: 'Barril' },
    { code: 'UN', description: 'Unidad' }
  ];

  private subscriptions: Subscription[] = [];
  initialValues: { [key: string]: any } = {};

  senderGeoValidating = false;
  senderGeoError: { municipalityName?: string; departmentName?: string; distanceMeters?: number } | null = null;
  recipientGeoValidating = false;
  recipientGeoError: { municipalityName?: string; departmentName?: string; distanceMeters?: number } | null = null;

  constructor(
    public menuService: MenuServiceService,
    public utils: Utils,
    public modelCargo: ModelCargo,
    public permissionRole: PermissionRole,
    private typeMerchandiseService: TypeMerchandiseService,
    public global: Global,
    public consignmentManager: ConsignmentManager,
    private cargoService: FreightListService,
    private geoValidation: GeoValidationService,
    private thirdPartyConsignmentService: ThirdPartyConsignmentService,
  ) { }

  async ngOnInit() {
    if (this.utils.isTeclogiUserOrClient) {
      this.timePactOptions = this.timePactOptions.filter(option =>
        this.utils.isTeclogiTimePactOption(option.hours, option.minutes));
    }
    this.setLocalValues();
    this.setSubscription();
  }

  private async initializeConsolidationTypes() {
    try {
      let response = await this.cargoService.getConsolidationTypes().toPromise();
      if (response && response.catalog && response.catalog.length) this.consolidationTypeOptions = response.catalog;
      else this.consolidationTypeOptions = [];
    } catch (error) {
      console.error('Error al obtener los tipos de consolidación:', error);
      this.consolidationTypeOptions = [];
    }
  }

  private async setLocalValues() {
    const cargoOwner = this.utils.getNestedValue(this.form.value, 'cargoOwner');
    this.internalForm.get('cargoOwnerNameControl').setValue(cargoOwner && cargoOwner.name ? cargoOwner.name : '-');
    this.internalForm.get('cargoOwnerDocumentControl').setValue(cargoOwner && cargoOwner.documentNumber ? cargoOwner.documentNumber : '-');
    this.internalForm.get('cargoOwnerBranchOfficeControl').setValue(cargoOwner && cargoOwner.branchOffice ? cargoOwner.branchOffice : '-');
    const operationType = this.utils.getNestedValue(this.form.value, 'operationType');
    this.checkConsignmentTypes();
    const consignmentType = this.filteredConsignmentTypeOptions.find(option => operationType && option.code === operationType.code);
    if (consignmentType) this.internalForm.get('operationTypeControl').setValue(consignmentType);
    this.checkFilteredPackagingTypes();
    if (operationType && operationType.code === 'P') {
      await this.initializeConsolidationTypes();
      const consolidationType = this.utils.getNestedValue(this.form.value, 'consolidationType');
      const consolidationTypeOption = this.consolidationTypeOptions.find(option => consolidationType && option.id === consolidationType.id);
      if (consolidationTypeOption) this.internalForm.get('consolidationTypeControl').setValue(consolidationTypeOption);
    }
    const cargoNature = this.utils.getNestedValue(this.form.value, 'cargoNature');
    const valueCargoNature = this.cargoNatureTypeOptions.find(option => cargoNature && option.code === cargoNature.code);
    if (valueCargoNature) this.internalForm.get('cargoNatureControl').setValue(valueCargoNature);
    const packagingType = this.utils.getNestedValue(this.form.value, 'packagingType');
    const valuePackagingType = this.filteredPackagingTypeOptions.find(option => packagingType && option.code === packagingType.code);
    if (valuePackagingType) this.internalForm.get('packagingTypeControl').setValue(valuePackagingType);
    const merchandiseDescription = this.utils.getNestedValue(this.form.value, 'merchandise.description');
    if (merchandiseDescription) this.initialValues.merchandiseDescription = merchandiseDescription;
    this.internalForm.get('chapterControl').setValue(this.utils.getNestedValue(this.form.value, 'chapter'));
    const subSection = this.utils.getNestedValue(this.form.value, 'subSection');
    if (subSection && subSection.code && subSection.description) this.initialValues.subSection = subSection;
    const tax = this.utils.getNestedValue(this.form.value, 'tax');
    if (tax && tax.code && tax.description) this.initialValues.tax = tax;
    const unProduct = this.utils.getNestedValue(this.form.value, 'dangerousMerchandise.unProduct');
    if (unProduct && unProduct.code && unProduct.description) this.initialValues.unProduct = unProduct;
    if (unProduct && unProduct.description) this.internalForm.get('UNDescriptionControl').setValue(unProduct.description);
    const dangerousMerchandiseState = this.utils.getNestedValue(this.form.value, 'dangerousMerchandise.merchandiseState');
    if (dangerousMerchandiseState && dangerousMerchandiseState.code && dangerousMerchandiseState.description)
      this.internalForm.get('productTypeStateControl').setValue(dangerousMerchandiseState);
    const hasPackaging = this.utils.getNestedValue(this.form.value, 'dangerousMerchandise.hasPackaging');
    const packaging = this.utils.getNestedValue(this.form.value, 'dangerousMerchandise.packaging');
    if (hasPackaging && packaging) this.internalForm.get('packagingGroupControl').setValue({ code: packaging });
    const waste = this.utils.getNestedValue(this.form.value, 'hazardousWaste.waste');
    if (waste && waste.code && waste.description) {
      this.internalForm.get('hazardousWasteCurrentControl').setValue(waste.description.charAt(0));
      this.initialValues.waste = waste;
      const hasDisaggregation = this.utils.getNestedValue(this.form.value, 'hazardousWaste.hasWasteDisaggregation');
      const wasteDisaggregation = this.utils.getNestedValue(this.form.value, 'hazardousWaste.wasteDisaggregation');
      if (hasDisaggregation && wasteDisaggregation && wasteDisaggregation.code && wasteDisaggregation.description)
        this.initialValues.wasteDisaggregation = wasteDisaggregation;
    }
    const amountProduct = this.utils.getNestedValue(this.form.value, 'amountProduct');
    if (amountProduct) this.internalForm.get('totalWeightControl').setValue(amountProduct);
    const weightUnit = this.utils.getNestedValue(this.form.value, 'unitMeasureProduct');
    if (weightUnit && weightUnit.code && weightUnit.description) this.internalForm.get('weightUnitControl').setValue(weightUnit);
    const senderCity = this.utils.getNestedValue(this.form.value, 'sender.city');
    const senderCityId = this.utils.getNestedValue(this.form.value, 'sender.cityId');
    const senderMunicipalityCode = this.utils.getNestedValue(this.form.value, 'sender.municipalityCode');
    const senderCityOption = Object.values(this.senderCityOptions).find(city => city && city.name === senderCity && city.index === senderCityId);
    if (senderCityOption && senderMunicipalityCode && senderCityOption.municipalityCode === senderMunicipalityCode) {
      this.internalForm.get('senderCityControl').setValue({ key: `${senderCityOption.index + 1}.${senderCity}`, value: senderCityOption });
      const senderDocumentType = this.utils.getNestedValue(this.form.value, 'sender.documentType');
      const senderDocumentNumber = this.utils.getNestedValue(this.form.value, 'sender.documentNumber');
      const senderThirdParty = senderCityOption.thirdParties.find(thirdParty => thirdParty && thirdParty.documentNumber === senderDocumentNumber && thirdParty.documentType === senderDocumentType);
      if (senderThirdParty) {
        this.internalForm.get('senderThirdParty').setValue(senderThirdParty);
        const senderAddress = this.utils.getNestedValue(this.form.value, 'sender.address');
        const senderAddressId = this.utils.getNestedValue(this.form.value, 'sender.addressId');
        const senderLocationLat = this.utils.getNestedValue(this.form.value, 'sender.location.lat');
        const senderLocationLng = this.utils.getNestedValue(this.form.value, 'sender.location.lng');
        const senderAddressOption = senderThirdParty.addresses.find(address => {
          return address && address.address === senderAddress && address.index === senderAddressId && address.location &&
            address.location.lat === senderLocationLat && address.location.lng === senderLocationLng;
        });
        if (senderAddressOption) {
          this.internalForm.get('senderAddressControl').setValue(senderAddressOption);
          this.form.get('load.appointmentTime').setValue(senderAddressOption.time ? senderAddressOption.time : null);
          if (!this.hasEntryId()) this.validateGeo('sender', senderAddressOption);
        }
      }
      const senderLoadDate = senderCityOption.date ? senderCityOption.date : null;
      if (senderLoadDate) {
        this.internalForm.get('loadDateControl').setValue(DateManager.stringToDate(senderLoadDate));
        this.form.get(`load.date`).setValue(DateManager.formatDate(senderLoadDate, null, 'DD/MM/YYYY ZZ'));
      } else {
        const loadDate = this.utils.getNestedValue(this.form.value, 'load.date');
        this.internalForm.get('loadDateControl').setValue(loadDate ? loadDate.includes('-') ? DateManager.stringToDate(loadDate, 'DD/MM/YYYY ZZ') : DateManager.stringToDate(loadDate, 'DD/MM/YYYY') : null);
      }
    } else {
      const loadDate = this.utils.getNestedValue(this.form.value, 'load.date');
      this.internalForm.get('loadDateControl').setValue(loadDate ? loadDate.includes('-') ? DateManager.stringToDate(loadDate, 'DD/MM/YYYY ZZ') : DateManager.stringToDate(loadDate, 'DD/MM/YYYY') : null);
    }
    let loadTimePactHours = this.utils.getNestedValue(this.form.value, 'load.hours');
    let loadTimePactMinutes = this.utils.getNestedValue(this.form.value, 'load.minutes');
    if (this.utils.isTeclogiUserOrClient && !this.utils.isDefined(loadTimePactHours) && !this.utils.isDefined(loadTimePactMinutes)) {
      const def = this.utils.getTeclogiDefaultTimePact();
      loadTimePactHours = def.hours;
      loadTimePactMinutes = def.minutes;
      this.form.get('load.hours').setValue(def.hours);
      this.form.get('load.minutes').setValue(def.minutes);
    }
    this.internalForm.get('loadTimePactControl').setValue(this.utils.isDefined(loadTimePactHours) && this.utils.isDefined(loadTimePactMinutes) ? { hours: loadTimePactHours, minutes: loadTimePactMinutes } : null);
    this.loadTimePactOptions = this.utils.isTeclogiUserOrClient
      ? this.utils.getTeclogiTimePactOptionsWithCurrent(this.timePactOptions, loadTimePactHours, loadTimePactMinutes)
      : this.timePactOptions;
    const recipientCity = this.utils.getNestedValue(this.form.value, 'recipient.city');
    const recipientMunicipalityCode = this.utils.getNestedValue(this.form.value, 'recipient.municipalityCode');
    const recipientCityId = this.utils.getNestedValue(this.form.value, 'recipient.cityId');
    const recipientCityOption = Object.values(this.recipientCityOptions).find(city => city && city.name === recipientCity && city.index === recipientCityId);
    if (recipientCityOption && recipientMunicipalityCode && recipientCityOption.municipalityCode === recipientMunicipalityCode) {
      this.internalForm.get('recipientCityControl').setValue({ key: `${recipientCityOption.index + 1}.${recipientCity}`, value: recipientCityOption });
      const recipientDocumentType = this.utils.getNestedValue(this.form.value, 'recipient.documentType');
      const recipientDocumentNumber = this.utils.getNestedValue(this.form.value, 'recipient.documentNumber');
      const recipientName = this.utils.getNestedValue(this.form.value, 'recipient.name');
      const recipientThirdParty = recipientCityOption.thirdParties.find(thirdParty => thirdParty && thirdParty.documentNumber === recipientDocumentNumber && thirdParty.documentType === recipientDocumentType && thirdParty.name === recipientName);
      if (recipientThirdParty) {
        this.internalForm.get('recipientThirdParty').setValue(recipientThirdParty);
        const recipientAddress = this.utils.getNestedValue(this.form.value, 'recipient.address');
        const recipientAddressId = this.utils.getNestedValue(this.form.value, 'recipient.addressId');
        const recipientLocationLat = this.utils.getNestedValue(this.form.value, 'recipient.location.lat');
        const recipientLocationLng = this.utils.getNestedValue(this.form.value, 'recipient.location.lng');
        const recipientAddressOption = recipientThirdParty.addresses.find(address => {
          return address && address.address === recipientAddress && address.index === recipientAddressId && address.location &&
            address.location.lat === recipientLocationLat && address.location.lng === recipientLocationLng;
        });
        if (recipientAddressOption) {
          this.internalForm.get('recipientAddressControl').setValue(recipientAddressOption);
          this.form.get('unload.appointmentTime').setValue(recipientAddressOption.time ? recipientAddressOption.time : null);
          if (!this.hasEntryId()) this.validateGeo('recipient', recipientAddressOption);
        }
      }
      const recipientUnloadDate = recipientCityOption.date ? recipientCityOption.date : null;
      if (recipientUnloadDate) {
        this.internalForm.get('unloadDateControl').setValue(DateManager.stringToDate(recipientUnloadDate));
        this.form.get(`unload.date`).setValue(DateManager.formatDate(recipientUnloadDate, null, 'DD/MM/YYYY ZZ'));
      } else {
        const unloadDate = this.utils.getNestedValue(this.form.value, 'unload.date');
        this.internalForm.get('unloadDateControl').setValue(unloadDate ? unloadDate.includes('-') ? DateManager.stringToDate(unloadDate, 'DD/MM/YYYY ZZ') : DateManager.stringToDate(unloadDate, 'DD/MM/YYYY') : null);
      }
    } else {
      const unloadDate = this.utils.getNestedValue(this.form.value, 'unload.date');
      this.internalForm.get('unloadDateControl').setValue(unloadDate ? unloadDate.includes('-') ? DateManager.stringToDate(unloadDate, 'DD/MM/YYYY ZZ') : DateManager.stringToDate(unloadDate, 'DD/MM/YYYY') : null);
    }
    let unloadTimePactHours = this.utils.getNestedValue(this.form.value, 'unload.hours');
    let unloadTimePactMinutes = this.utils.getNestedValue(this.form.value, 'unload.minutes');
    if (this.utils.isTeclogiUserOrClient && !this.utils.isDefined(unloadTimePactHours) && !this.utils.isDefined(unloadTimePactMinutes)) {
      const def = this.utils.getTeclogiDefaultTimePact();
      unloadTimePactHours = def.hours;
      unloadTimePactMinutes = def.minutes;
      this.form.get('unload.hours').setValue(def.hours);
      this.form.get('unload.minutes').setValue(def.minutes);
    }
    this.internalForm.get('unloadTimePactControl').setValue(this.utils.isDefined(unloadTimePactHours) && this.utils.isDefined(unloadTimePactMinutes) ? { hours: unloadTimePactHours, minutes: unloadTimePactMinutes } : null);
    this.unloadTimePactOptions = this.utils.isTeclogiUserOrClient
      ? this.utils.getTeclogiTimePactOptionsWithCurrent(this.timePactOptions, unloadTimePactHours, unloadTimePactMinutes)
      : this.timePactOptions;
    if (this.hasEntryId()) this.disableFieldsOnEmittedConsignment();
    this.changeValidatorsTotalWeight();
  }

  hasEntryId(): boolean {
    return this.form.get('entryId') && this.form.get('entryId').value;
  }

  private disableFieldsOnEmittedConsignment() {
    this.form.disable({ emitEvent: false });
    this.internalForm.disable({ emitEvent: false });
  }

  private setSubscription() {
    this.subscriptions.push(
      this.internalForm.get('merchandiseControl').valueChanges.subscribe(value => this.onChangeMerchandise(value)),
      this.internalForm.get('subHeaderControl').valueChanges.subscribe(value => this.onChangeSubHeader(value)),
      this.internalForm.get('tariffControl').valueChanges.subscribe(value => this.onChangeTariff(value)),
      this.internalForm.get('UNCodeControl').valueChanges.subscribe(value => this.onSelectUNCode(value)),
      this.internalForm.get('hazardousWasteCurrentDescriptionControl').valueChanges.subscribe(value => this.onChangeHazardousWasteCurrentDescription(value)),
      this.internalForm.get('disintegrationControl').valueChanges.subscribe(value => this.onChangeDisintegration(value)),
      this.internalForm.get('totalWeightControl').valueChanges.subscribe(value => this.onChangeTotalWeight(value)),
      this.internalForm.get('loadDateControl').valueChanges.subscribe(value => this.onChangeDate(value, 'load')),
      this.internalForm.get('unloadDateControl').valueChanges.subscribe(value => this.onChangeDate(value, 'unload'))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formValidate']) {
      if (this.formValidate === 'touched') {
        this.internalForm.markAsTouched();
        this.form.markAllAsTouched();
      }
      if (this.formValidate === 'disable') {
        this.internalForm.disable();
        this.form.disable();
      }
      if (this.formValidate === 'enable') {
        this.internalForm.enable();
        this.internalForm.get('cargoOwnerNameControl').disable();
        this.internalForm.get('cargoOwnerDocumentControl').disable();
        this.internalForm.get('cargoOwnerBranchOfficeControl').disable();
        this.internalForm.get('chapterControl').disable();
        this.internalForm.get('UNDescriptionControl').disable();
        this.form.enable();
        if (this.hasEntryId()) this.disableFieldsOnEmittedConsignment();
      }

    }
    if (changes['cargo'])
      this.checkConsignmentTypes();
  }

  get isContainerService(): boolean {
    if (this.cargo)
      return this.utils.getNestedValue(this.cargo, 'cargoModel.cargoType.name') === 'Contenedor';
    return this.menuService.isContainerService();
  }

  compareByCode(a: { code: string }, b: { code: string }) {
    return a && b && a.code === b.code;
  }

  compareByDescription(a: { description: string }, b: { description: string }) {
    return a && b && a.description === b.description;
  }

  compareById(a: { id: string }, b: { id: string }) {
    return a && b && a.id === b.id;
  }

  compareTimePact(a: { hours: number, minutes: number }, b: { hours: number, minutes: number }) {
    return a && b && a.hours === b.hours && a.minutes === b.minutes;
  }

  compareByKey(a: { key: string }, b: { key: string }) {
    return a && b && a.key === b.key;
  }

  isDisabledCity(city: string, option: ConsignmentCityOption): string {
    if (!city || !option) return 'No se encontró la ciudad';
    if (option.countryCode === 'CO' && !option.municipalityCode) return 'Se requiere diligenciar la ciudad del servicio nuevamente';
    if (option.countryCode !== 'CO') return 'No se pueden generar remesas con ciudades de países diferentes a Colombia';
    if (!option.thirdParties || !option.thirdParties.length) return 'La ciudad no tiene terceros asociados';
    if (!option.date) return 'Se requiere diligenciar la fecha de llegada a la ciudad';
    return '';
  }

  isDisabledThirdParty(thirdParty: ConsignmentThirdPartyOption): string {
    if (!thirdParty || !thirdParty.documentNumber || !thirdParty.documentType || !thirdParty.name) return 'No se encontró el tercero';
    if (!thirdParty.addresses || !thirdParty.addresses.length) return 'El tercero no tiene direcciones asociadas';
    return '';
  }

  isDisabledAddress(address: string, option: { address: string; location: LocationAddress }): string {
    if (!address || !option || !option.address) return 'No se encontró la dirección';
    if (!option.location.lat || !option.location.lng) return 'Se requiere diligenciar la dirección nuevamente';
    return '';
  }

  private checkConsignmentTypes() {
    const cargoType = this.utils.getNestedValue(this.cargo, 'cargoModel.cargoType.name');
    const isContainer = cargoType === 'Contenedor';
    const serviceTypeName = this.utils.getNestedValue(this.cargo.cargoModel, 'serviceType.name');
    const isEmptyContainer = ['Contenedor vacío con carga de compensación', 'Contenedor vacío express'].includes(serviceTypeName);
    if (isContainer && isEmptyContainer) //Contenedor vacío
      this.filteredConsignmentTypeOptions = this.consignmentTypeOptions.filter(option => option.code === 'V');
    else if (isContainer && !isEmptyContainer)  //Contenedor cargado
      this.filteredConsignmentTypeOptions = this.consignmentTypeOptions.filter(option => option.code === 'C');
    else //General
      this.filteredConsignmentTypeOptions = this.consignmentTypeOptions.filter(option => option.code === 'G' || option.code === 'P');

    if (this.internalForm.get('operationTypeControl').value && this.internalForm.get('operationTypeControl').value.code &&
      !this.filteredConsignmentTypeOptions.some(option => option.code === this.internalForm.get('operationTypeControl').value.code)) {
      this.internalForm.get('operationTypeControl').setValue(null);
      this.onChangeOperationType({ code: '', description: '', tooltip: '' });
    }
  }

  async onChangeOperationType(event: { code: string, description: string, tooltip: string }) {
    this.form.get('operationType').patchValue(event);
    this.checkFilteredPackagingTypes();
    if (!this.consignmentManager.consignmentRequiresConsolidationType(this.form.getRawValue())) {
      this.form.get('consolidationType.id').setValue('');
      this.form.get('consolidationType.name').setValue('');
      this.internalForm.get('consolidationTypeControl').setValue('');
    }
    if (event.code === 'P') await this.initializeConsolidationTypes();

  }

  onChangeConsolidationType(event: { id: string, name: string }) {
    this.form.get('consolidationType').patchValue(event);
  }

  private checkFilteredPackagingTypes() {
    if (!this.internalForm.get('operationTypeControl').value || !this.internalForm.get('operationTypeControl').value.code)
      this.filteredPackagingTypeOptions = this.packagingTypeOptions.map(option => ({ code: option.code, description: option.description }));
    else {
      const code = this.internalForm.get('operationTypeControl').value.code;
      let options: Array<{ code: string, description: string }> = [];
      if (['C', 'V'].includes(code)) //Para contenedores
        options = this.packagingTypeOptions.filter(option => !!option.hidden);
      else if (code === 'G') // General
        options = this.packagingTypeOptions.filter(option => !option.hidden);
      else if (code === 'P') //Consolidada
        options = this.packagingTypeOptions.filter(option => option.code === '0' || option.code === '17');
      if (options.length) this.filteredPackagingTypeOptions = options.map(option => ({ code: option.code, description: option.description }));
      else this.filteredPackagingTypeOptions = [];
    }
    if (this.internalForm.get('packagingTypeControl').value && this.internalForm.get('packagingTypeControl').value.code &&
      !this.filteredPackagingTypeOptions.some(option => option.code === this.internalForm.get('packagingTypeControl').value.code)) {
      this.internalForm.get('packagingTypeControl').setValue(null);
      this.onChangePackagingType({ code: '', description: '' });
    }
  }

  get isUrbanConsignment(): boolean {
    if (!this.senderCityOptions || !this.recipientCityOptions) return false;
    if (Object.keys(this.senderCityOptions).length === 0 || Object.keys(this.recipientCityOptions).length === 0) return false;
    const firstKeySender = Object.keys(this.senderCityOptions)[0];
    if (!firstKeySender) return false;
    const senderMunicipalityCode = this.utils.getNestedValue(this.senderCityOptions[firstKeySender], 'municipalityCode');
    if (!senderMunicipalityCode) return false;
    return Object.values(this.senderCityOptions).every(option => option && option.municipalityCode === senderMunicipalityCode) &&
      Object.values(this.recipientCityOptions).every(option => option && option.municipalityCode === senderMunicipalityCode);
  }

  disableNatureType(natureType: { code: string, description: string }): boolean {
    if (!this.isUrbanConsignment) return false;
    return ['2', '5'].includes(natureType.code);
  }

  get isDangerous(): boolean {
    return this.consignmentManager.consignmentHasDangerousNature(this.form.getRawValue());
  }

  private initializeDangerousFields() {
    if (!this.form.get('dangerousMerchandise'))
      this.form.addControl('dangerousMerchandise', new FormGroup({
        unProduct: new FormControl('', Validators.required),
        merchandiseState: new FormControl('', Validators.required),
        hasPackaging: new FormControl(false),
        packaging: new FormControl(''),
        nepName: new FormControl('')
      }));
  }

  get isHazardous(): boolean {
    return this.consignmentManager.consignmentHasHazardousWaste(this.form.getRawValue());
  }

  private initializeHazardousFields() {
    if (!this.form.get('hazardousWaste'))
      this.form.addControl('hazardousWaste', new FormGroup({
        hazardousWasteDescription: new FormControl('', Validators.required),
        waste: new FormControl('', Validators.required),
        hazardLevel: new FormControl('', Validators.required),
        hasWasteDisaggregation: new FormControl(false),
        wasteDisaggregation: new FormControl('')
      }));
  }

  onChangeCargoNature(event: { code: string, description: string }) {
    this.form.get('cargoNature').patchValue(event);
    if (!this.form.get('isDangerous')) this.form.addControl('isDangerous', new FormControl(this.isDangerous));
    else this.form.get('isDangerous').setValue(this.isDangerous);
    if (!this.form.get('isHazardousWaste')) this.form.addControl('isHazardousWaste', new FormControl(this.isHazardous));
    else this.form.get('isHazardousWaste').setValue(this.isHazardous);
    if (!this.form.get('productDescription')) this.form.addControl('productDescription', new FormControl('', [Validators.required, Validators.maxLength(60)]));
    else if (!this.consignmentManager.consignmentRequiresDescription(this.form.getRawValue())) this.form.get('productDescription').setValue('');
    //Se limpian o inicializan los campos de mercancía peligrosa
    this.initializeHazardousFields();
    if (!this.isHazardous) {
      this.internalForm.get('hazardousWasteCurrentControl').setValue('');
      this.internalForm.get('hazardousWasteCurrentDescriptionControl').setValue('');
      this.internalForm.get('disintegrationControl').setValue('');
    }
    //Se limpian o inicializan los campos de mercancía peligrosa desechos peligrosos
    if (!this.isDangerous) {
      this.internalForm.get('UNCodeControl').setValue('');
      this.internalForm.get('UNDescriptionControl').setValue('');
      this.internalForm.get('productTypeStateControl').setValue('');
      this.internalForm.get('packagingGroupControl').setValue('');
      this.packagingGroupOptions = [];
    }
    this.changeValidatorsTotalWeight();
  }

  onChangePackagingType(event: { code: string, description: string }) {
    this.form.get('packagingType').patchValue(event);
    this.changeValidatorsTotalWeight();
  }

  private changeValidatorsTotalWeight() {
    const requiresAmountProduct = this.consignmentManager.consignmentRequiresAmountProduct(this.form.getRawValue());
    this.internalForm.get('totalWeightControl').setValidators(requiresAmountProduct ? [Validators.required] : []);
    this.internalForm.get('totalWeightControl').updateValueAndValidity();
    this.internalForm.get('weightUnitControl').setValidators(requiresAmountProduct ? [Validators.required] : []);
    this.internalForm.get('weightUnitControl').updateValueAndValidity();
  }

  private onChangeMerchandise(event: MerchandiseType) {
    this.form.get('merchandise.code').setValue(event && event.code ? event.code : '');
    this.form.get('merchandise.description').setValue(event && event.description ? event.description : '');
    //Capítulo	
    const chapter = event && event.chapter ? event.chapter : '';
    if (!this.form.get('chapter')) this.form.addControl('chapter', new FormControl(chapter));
    else this.form.get('chapter').setValue(chapter);
    this.internalForm.get('chapterControl').setValue(chapter);
    //Sección
    const section = event && event.section ? event.section : '';
    if (!this.form.get('section')) this.form.addControl('section', new FormControl(section));
    else this.form.get('section').setValue(section);
    //Requiere subsección
    const requiresSubSection = event && event.requiresSubSection ? event.requiresSubSection : false;
    if (!this.form.get('requiresSubSection')) this.form.addControl('requiresSubSection', new FormControl(requiresSubSection));
    else this.form.get('requiresSubSection').setValue(requiresSubSection);
    //Subsección
    if (!this.form.get('subSection')) this.form.addControl('subSection', new FormControl(''));
    if (!requiresSubSection) this.form.get('subSection').setValue('');
    this.internalForm.get('subHeaderControl').setValue('');
    this.initialValues.subSection = { code: '', description: '' };
    //Requiere código de arancel
    if (!this.form.get('requiresTaxCode')) this.form.addControl('requiresTaxCode', new FormControl(false));
    else this.form.get('requiresTaxCode').setValue(false);
    //Código de arancel
    if (!this.form.get('tax')) this.form.addControl('tax', new FormControl(''));
    else this.form.get('tax').setValue('');
    this.internalForm.get('tariffControl').setValue('');
    this.initialValues.tax = { code: '', description: '' };
  }

  getCode(type: string): MerchandiseCodes {
    return type && type in MerchandiseCodes ? MerchandiseCodes[type] : MerchandiseCodes.BASIC_MERCHANDISE;
  }

  private onChangeSubHeader(value: MerchandiseType) {
    const initialSubsection: { code: string, description: string } = this.utils.getNestedValue(this.initialValues, 'subSection');
    if (initialSubsection && initialSubsection.code && initialSubsection.description &&
      value && value.subSectionCode === initialSubsection.code && value.description === initialSubsection.description) {
      delete this.initialValues.subSection;
      return;
    }
    //Subsección
    const subSectionCode = value && value.subSectionCode ? value.subSectionCode : '';
    const subSectionDescription = value && value.description ? value.description : '';
    const subSection = subSectionCode && subSectionDescription ? { code: subSectionCode, description: subSectionDescription } : '';
    if (!this.form.get('subSection')) this.form.addControl('subSection', new FormControl(subSection));
    else this.form.get('subSection').setValue(subSection);
    //Requiere código de arancel
    const requiresTaxCode = value && value.requiresTax ? value.requiresTax : false;
    if (!this.form.get('requiresTaxCode')) this.form.addControl('requiresTaxCode', new FormControl(requiresTaxCode));
    else this.form.get('requiresTaxCode').setValue(requiresTaxCode);
    //Código de arancel
    if (!this.form.get('tax')) this.form.addControl('tax', new FormControl(''));
    else this.form.get('tax').setValue('');
    this.initialValues.tax = { code: '', description: '' };
    this.internalForm.get('tariffControl').setValue('');
  }

  private onChangeTariff(value: MerchandiseType) {
    const initialTax: { code: string, description: string } = this.utils.getNestedValue(this.initialValues, 'tax');
    if (initialTax && initialTax.code && initialTax.description &&
      value && value.taxCode === initialTax.code && value.description === initialTax.description) {
      delete this.initialValues.tax;
      return;
    }
    const taxCode = value && value.taxCode ? value.taxCode : '';
    const taxDescription = value && value.description ? value.description : '';
    const tax = taxCode && taxDescription ? { code: taxCode, description: taxDescription } : '';
    if (!this.form.get('tax')) this.form.addControl('tax', new FormControl(tax));
    else this.form.get('tax').setValue(tax);
  }

  cleanOnChange(control: string) {
    if (this[control]) this[control].setValue('');
  }

  onSelectUNCode(value?: MerchandiseType) {
    const initialUNCode: { code: string, description: string } = this.utils.getNestedValue(this.initialValues, 'unProduct');
    if (initialUNCode && initialUNCode.code && initialUNCode.description) {
      delete this.initialValues.unProduct;
      this.getPackagingGroupOptions(value);
      return;
    }
    const unCode = value && value.code ? value.code : '';
    const description = value && value.description ? value.description : '';
    const unProduct = unCode && description ? { code: unCode, description: description } : '';
    if (!this.form.get('dangerousMerchandise')) this.initializeDangerousFields();
    this.form.get('dangerousMerchandise.unProduct').setValue(unProduct);
    this.internalForm.get('UNDescriptionControl').setValue(description);
    if (!description || !description.includes('N.E.P.'))
      this.form.get('dangerousMerchandise.nepName').setValue('');
    this.internalForm.get('packagingGroupControl').setValue('');
    this.form.get('dangerousMerchandise.packaging').setValue('');
    this.getPackagingGroupOptions(value);
  }

  private getPackagingGroupOptions(unCodeValue?: MerchandiseType) {
    this.packagingGroupOptions = [];
    if (!unCodeValue || !unCodeValue.packaging)
      return this.form.get('dangerousMerchandise.hasPackaging').setValue(false);
    const packaging: string = unCodeValue.packaging;
    const items = packaging.split(',').map(item => {
      if (!item || !item.trim()) return null;
      const packagingOption = this.packagingOptions.find(option => option.code === item.trim());
      return packagingOption ? packagingOption : null;
    }).filter(item => !!item);
    this.packagingGroupOptions = items;
    const hasPackaging = !!items.length;
    this.form.get('dangerousMerchandise.hasPackaging').setValue(hasPackaging);
  }

  onChangeProductTypeState(value: { code: string, description: string, label: string }) {
    if (!this.form.get('dangerousMerchandise')) this.initializeDangerousFields();
    this.form.get('dangerousMerchandise.merchandiseState').setValue({ code: value.code, description: value.description });
    this.changeValidatorsTotalWeight();
  }

  onChangePackagingGroup(value: { code: string, description: string }) {
    if (!this.form.get('dangerousMerchandise')) this.initializeDangerousFields();
    this.form.get('dangerousMerchandise.packaging').setValue(value.code);
  }

  onChangeHazardousWasteCurrent() {
    this.internalForm.get('hazardousWasteCurrentDescriptionControl').setValue('');
    if (!this.form.get('hazardousWaste')) this.initializeHazardousFields();
    this.form.get('hazardousWaste.waste').setValue('');
    this.initialValues.waste = { code: '', description: '' };
    this.internalForm.get('disintegrationControl').setValue('');
    this.form.get('hazardousWaste.wasteDisaggregation').setValue('');
    this.initialValues.wasteDisaggregation = { code: '', description: '' };
  }

  private async onChangeHazardousWasteCurrentDescription(value: MerchandiseType) {
    const initialWaste: { code: string, description: string } = this.utils.getNestedValue(this.initialValues, 'waste');
    if (initialWaste && initialWaste.code && initialWaste.description &&
      value && value.code === initialWaste.code && value.description === initialWaste.description) {
      delete this.initialValues.waste;
      return;
    }
    const code = value && value.code ? value.code : '';
    const description = value && value.description ? value.description : '';
    const waste = code && description ? { code: code, description: description } : '';
    if (!this.form.get('hazardousWaste')) this.initializeHazardousFields();
    this.form.get('hazardousWaste.waste').setValue(waste);

    let requiresDisaggregation = false;
    if (code.includes('Y')) {
      try {
        const response = await this.typeMerchandiseService.searchMerchandise(null, this.getCode('DISAGREGATION'), 'Y', code).toPromise();
        requiresDisaggregation = !!(response && response.length);
      } catch (error) {
        requiresDisaggregation = false;
      }
    }
    this.form.get('hazardousWaste.hasWasteDisaggregation').setValue(requiresDisaggregation);
    this.form.get('hazardousWaste.wasteDisaggregation').setValue('');
    this.initialValues.wasteDisaggregation = { code: '', description: '' };
  }

  private onChangeDisintegration(value: MerchandiseType) {
    const code = value && value.subHazardousCode ? value.subHazardousCode : '';
    const description = value && value.description ? value.description : '';
    const wasteDisaggregation = code && description ? { code: code, description: description } : '';
    if (!this.form.get('hazardousWaste')) this.initializeHazardousFields();
    this.form.get('hazardousWaste.wasteDisaggregation').setValue(wasteDisaggregation);
  }

  onChangeTotalWeight(value: number | string) {
    if (this.form.get('amountProduct')) this.form.get('amountProduct').setValue(Number(value));
    else this.form.addControl('amountProduct', new FormControl(Number(value)));
  }

  onChangeWeightUnit(value: { code: string, description: string }) {
    if (!this.form.get('unitMeasureProduct')) this.form.addControl('unitMeasureProduct', new FormControl(''));
    this.form.get('unitMeasureProduct').setValue({ code: value.code, description: value.description });
  }

  onChangeDate(event: Date, type: 'load' | 'unload') {
    this.form.get(`${type}.date`).setValue(DateManager.dateToString(event, 'DD/MM/YYYY ZZ'));
  }

  onChangeTimePact(event: { hours: number, minutes: number }, type: 'load' | 'unload') {
    this.form.get(`${type}.hours`).setValue(event.hours);
    this.form.get(`${type}.minutes`).setValue(event.minutes);
  }

  onChangeCity(city: { key: string, value: ConsignmentCityOption }, type: 'sender' | 'recipient') {
    this.clearGeoState(type);
    this.form.get(`${type}.city`).setValue(city.value.name);
    const cityOption = type === 'sender' ? this.senderCityOptions[city.key] : this.recipientCityOptions[city.key];
    this.form.get(`${type}.municipalityCode`).setValue(city.value.municipalityCode);
    this.form.get(`${type}.cityId`).setValue(city.value.index);
    type === 'sender' && this.form.get('sender.fromDestination').setValue(city.value.type === 'destination');
    if (cityOption && cityOption.date) {
      const date = DateManager.stringToDate(cityOption.date);
      this.form.get(type === 'sender' ? 'load.date' : 'unload.date').setValue(DateManager.dateToString(date, 'DD/MM/YYYY ZZ'));
      type === 'sender' ? this.internalForm.get('loadDateControl').setValue(date) : this.internalForm.get('unloadDateControl').setValue(date);
    }
    this.form.get(`${type}.documentType`).setValue("");
    this.form.get(`${type}.documentNumber`).setValue("");
    this.form.get(`${type}.name`).setValue("");
    this.form.get(`${type}.branchOffice`).setValue("");
    this.form.get(`${type}.address`).setValue("");
    this.form.get(`${type}.addressId`).setValue("");
    this.form.get(`${type}.location`).patchValue({ lat: "", lng: "" });
    if (type === 'sender') {
      this.internalForm.get('senderAddressControl').setValue(null);
      this.internalForm.get('senderThirdParty').setValue(null);
    } else {
      this.internalForm.get('recipientAddressControl').setValue(null);
      this.internalForm.get('recipientThirdParty').setValue(null);
    }
  }

  onChangeThirdParty(event: ConsignmentThirdPartyOption, type: 'sender' | 'recipient') {
    this.clearGeoState(type);
    this.form.get(`${type}.documentType`).setValue(event && event.documentType ? event.documentType : '');
    this.form.get(`${type}.documentNumber`).setValue(event && event.documentNumber ? event.documentNumber : '');
    this.form.get(`${type}.name`).setValue(event && event.name ? event.name : '');
    this.form.get(`${type}.branchOffice`).setValue("");
    this.form.get(`${type}.address`).setValue("");
    this.form.get(`${type}.addressId`).setValue("");
    this.form.get(`${type}.location`).patchValue({ lat: "", lng: "" });
    if (type === 'sender') this.internalForm.get('senderAddressControl').setValue(null);
    else this.internalForm.get('recipientAddressControl').setValue(null);
  }

  onChangeAddress(address: ConsignmentAddressOption, type: 'sender' | 'recipient') {
    this.form.get(`${type}.address`).setValue(address.address);
    this.form.get(`${type}.addressId`).setValue(address.index);
    this.form.get(`${type}.location`).patchValue(address.location);
    this.form.get(`${type}.branchOffice`).setValue(address.branchOffice);
    if (address && address.time)
      this.form.get(type === 'sender' ? 'load.appointmentTime' : 'unload.appointmentTime').setValue(address.time);
    if (type === 'sender' && this.internalForm.get('senderCityControl').value && this.internalForm.get('senderCityControl').value.value && this.internalForm.get('senderCityControl').value.value.type)
      this.form.get('sender.fromDestination').setValue(this.internalForm.get('senderCityControl').value.value.type === 'destination');
    this.validateGeo(type, address);
  }

  validateGeo(type: 'sender' | 'recipient', address: ConsignmentAddressOption): void {
    const cityId = this.form.get(`${type}.municipalityCode`) && this.form.get(`${type}.municipalityCode`).value;
    const lat = address && address.location && address.location.lat;
    const lng = address && address.location && address.location.lng;
    if (!cityId || !lat || !lng) return;
    const ctrlName = type === 'sender' ? 'senderAddressControl' : 'recipientAddressControl';
    const formAddressCtrl = this.form.get(`${type}.address`);
    if (type === 'sender') { this.senderGeoValidating = true; this.senderGeoError = null; }
    else { this.recipientGeoValidating = true; this.recipientGeoError = null; }
    this.geoValidation.validate({ cityId, lat: +lat, lng: +lng }).pipe(
      take(1),
      catchError(() => of({ valid: true } as any)),
      finalize(() => {
        if (type === 'sender') this.senderGeoValidating = false;
        else this.recipientGeoValidating = false;
      })
    ).subscribe(resp => {
      if (resp.valid) {
        this.internalForm.get(ctrlName).setErrors(null);
        if (type === 'sender') this.senderGeoError = null;
        else this.recipientGeoError = null;
        if (formAddressCtrl) {
          const currentErrors = formAddressCtrl.errors;
          if (currentErrors && currentErrors['outsideMunicipality']) {
            const { outsideMunicipality, ...rest } = currentErrors as any;
            formAddressCtrl.setErrors(Object.keys(rest).length ? rest : null, { emitEvent: false });
          }
        }
      } else {
        const err = { municipalityName: resp.municipalityName, departmentName: resp.departmentName, distanceMeters: resp.distanceToBoundaryMeters };
        if (type === 'sender') this.senderGeoError = err;
        else this.recipientGeoError = err;
        const current = this.internalForm.get(ctrlName).errors || {};
        this.internalForm.get(ctrlName).setErrors({ ...current, outsideMunicipality: err });
        if (formAddressCtrl) {
          const currentErrors = formAddressCtrl.errors || {};
          formAddressCtrl.setErrors({ ...currentErrors, outsideMunicipality: err }, { emitEvent: false });
        }
      }
    });
  }

  private clearGeoState(type: 'sender' | 'recipient'): void {
    const ctrlName = type === 'sender' ? 'senderAddressControl' : 'recipientAddressControl';
    if (type === 'sender') { this.senderGeoError = null; this.senderGeoValidating = false; }
    else { this.recipientGeoError = null; this.recipientGeoValidating = false; }
    const ctrl = this.internalForm.get(ctrlName);
    if (ctrl && ctrl.errors) {
      const { outsideMunicipality, ...rest } = ctrl.errors as any;
      ctrl.setErrors(Object.keys(rest).length ? rest : null);
    }
    const formAddressCtrl = this.form.get(`${type}.address`);
    if (formAddressCtrl && formAddressCtrl.errors && formAddressCtrl.errors['outsideMunicipality']) {
      const { outsideMunicipality, ...rest } = formAddressCtrl.errors as any;
      formAddressCtrl.setErrors(Object.keys(rest).length ? rest : null, { emitEvent: false });
    }
  }

  editGeoAddress(type: 'sender' | 'recipient'): void {
    const thirdPartyCtrl = type === 'sender' ? 'senderThirdParty' : 'recipientThirdParty';
    const addressCtrl = type === 'sender' ? 'senderAddressControl' : 'recipientAddressControl';
    const selectedThirdParty: ConsignmentThirdPartyOption = this.internalForm.get(thirdPartyCtrl).value;
    const selectedAddress: ConsignmentAddressOption = this.internalForm.get(addressCtrl).value;
    if (!selectedThirdParty || !selectedAddress) return;
    const thirdParty: ThirdParty = {
      document: selectedThirdParty.documentNumber,
      documentType: selectedThirdParty.documentType,
      name: selectedThirdParty.name,
    };
    const thirdPartyAddress: ThirdPartyAddress = {
      information: { documentTypeId: selectedThirdParty.documentType, document: selectedThirdParty.documentNumber, name: selectedThirdParty.name } as any,
      address: { id: selectedAddress.branchOffice, address: selectedAddress.address, location: selectedAddress.location } as any,
      municipalityCode: this.form.get(`${type}.municipalityCode`).value,
      branchOfficeName: selectedAddress.address,
    };
    this.thirdPartyConsignmentService.editThirdPartyAddressDialog(
      thirdParty,
      thirdPartyAddress,
      { disableCityEdition: true, alertMessage: 'La dirección debe quedar dentro del municipio del cargo.' },
      (updated?: ThirdPartyAddress) => {
        if (!updated) return;
        const newLoc = updated.address && updated.address.location;
        if (newLoc && newLoc.lat && newLoc.lng) {
          this.form.get(`${type}.location`).patchValue({ lat: newLoc.lat, lng: newLoc.lng });
          this.form.get(`${type}.address`).setValue(updated.address.address || selectedAddress.address);
        }
        this.validateGeo(type, {
          ...selectedAddress,
          address: (updated.address && updated.address.address) || selectedAddress.address,
          location: { lat: Number(newLoc && newLoc.lat), lng: Number(newLoc && newLoc.lng) },
        });
      }
    );
  }

  get canEditConsignmentServiceModule(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.editConsignmentServiceModule
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
